'use client';
import {
  Step as MuiStep,
  Stack,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  type TypographyProps,
} from '@unity/cloud-ui';
import React from 'react';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

const labelComponentMap = {
  p: (props: Partial<TypographyProps>) => <Typography variant="body1" component="p" {...props} />,
  h3: (props: Partial<TypographyProps>) => <Typography variant="h3" {...props} />,
  h2: (props: Partial<TypographyProps>) => <Typography variant="h2" {...props} />,
};

export type TitleSize = keyof typeof labelComponentMap;

type StepProps = z.input<typeof stepSchema> & {
  titleSize?: TitleSize;
};

export const Step = (props: StepProps) => {
  const { title, titleSize = 'h3', children, ...rest } = props;
  const LabelComponent = labelComponentMap[titleSize];

  return (
    <MuiStep active expanded {...rest}>
      <StepLabel sx={{ '.MuiStepLabel-iconContainer': { zIndex: 0 } }}>
        <LabelComponent>{title}</LabelComponent>
      </StepLabel>
      <StepContent>
        <Stack spacing={1.5}>{children}</Stack>
      </StepContent>
    </MuiStep>
  );
};

export const Steps = (props: z.input<typeof stepsSchema>) => {
  const childrenWithProps = React.Children.map(props.children, (child) => {
    if (React.isValidElement<typeof Step>(child)) {
      const newStepProps: Partial<typeof Step> = { titleSize: props.titleSize };
      return React.cloneElement(child, newStepProps);
    }
    return child;
  });

  return (
    <Stepper nonLinear orientation="vertical" activeStep={-1}>
      {childrenWithProps}
    </Stepper>
  );
};

const documentationURL = getComponentDocumentationUrl('steps');

const descriptions = {
  stepSchema: {
    title: 'The title of the step.',
    children: 'The content of the step. Can be anything.',
    schema: `A single step with the number of the step.\n\n\nLists of \`<Step/>\` components must be wrapped in a \`<Steps/>\`.\n\n\n**Example**\n\`\`\`mdx\n<Steps>\n  <Step title="Step 1">\n    The content of the step\n  </Step>\n</Steps>\n\`\`\`\n\n${documentationURL}`,
  },
  stepsSchema: {
    children: 'The list of steps.',
    titleSize: 'The size of all the step titles.',
    schema: `The \`<Steps/>\` and child \`<Step/>\` components can be used to create a vertical list of ordered instructions.\n\n\n\n**Example**\n\`\`\`mdx\n<Steps>\n  <Step title="Step 1">\n    The content of the step\n  </Step>\n</Steps>\n\`\`\`\n\n${documentationURL}`,
  },
};

export const stepSchema = z
  .object({
    title: z.string().describe(descriptions.stepSchema.title),
    children: z.any().describe(descriptions.stepSchema.children),
  })
  .describe(descriptions.stepSchema.schema);

export const stepsSchema = z
  .object({
    children: z.any().describe(descriptions.stepsSchema.children),
    titleSize: z.enum(['p', 'h2', 'h3']).describe(descriptions.stepsSchema.titleSize).optional().default('h2'),
  })
  .describe(descriptions.stepsSchema.schema);
